import { z } from "zod";
import { BaseFirestoreWStatusTagsSchema } from "./firestore.schema";
import { AttachmentSchema } from "./attachment.schema";
import { DateTypeSchema } from "./core.schema";

// Schemas

export const UploadTypeIdSchema = z.enum([
  "administrative-records",
  "personnel-records",
  "tax-records",
  "legal-records",
  "elections-records",
  "court-records",
  "property-records",
  "meeting-minutes",
  "meeting-agenda",
  "ordinances",
  "resolutions",
  "zoning-planning-records",
  "newspaper-clipping",
  "reference-document",
]);

export const OrdinanceStatusSchema = z.enum(["proposed", "passed", "failed"]);
export const ResolutionStatusSchema = z.enum(["proposed", "passed", "failed"]);

export const OrdinanceDataSchema = z.object({
  number: z.string().optional(),
  draftNumber: z.string().optional(),
  category: z.string().optional(),
  status: OrdinanceStatusSchema.optional(),
});

export const ResolutionDataSchema = z.object({
  number: z.string().optional(),
  status: ResolutionStatusSchema.optional(),
});

export const DocumentMinutesDataSchema = z.object({
  meetingId: z.string().optional(),
  revision: z.number().optional(),
  official: z.boolean().optional(),
});

export const DocumentAgendaDataSchema = z.object({
  meetingId: z.string().optional(),
  official: z.boolean().optional(),
});

export const SubmissionStatusSchema = z.enum([
  "draft",
  "sent",
  "declined",
  "completed",
  "archived",
]);

export const SubmitterStatusSchema = z.enum([
  "pending",
  "sent",
  "opened",
  "completed",
  "declined",
  "skipped",
]);

export const SubmitterSchema = z.object({
  id: z.number().int(),
  email: z.string(), // .email(): TSOA won't apply email validation
  name: z.string().optional(),
  role: z.string().optional(),
  status: SubmitterStatusSchema,
  lastStatusChange: DateTypeSchema.optional(),
});

export const SubmissionSchema = z.object({
  id: z.number().int(),
  createdAt: z.string(),
  status: SubmissionStatusSchema,
  submitters: SubmitterSchema.array().optional(),
});

export const AddSubmitterDtoSchema = z.object({
  id: z.number().int(),
  email: z.string(), // .email(): TSOA won't apply email validation
  name: z.string().optional(),
  role: z.string().optional(),
  status: SubmitterStatusSchema.optional(),
});

export const AddSubmissionDtoSchema = z.object({
  id: z.number().int(),
  createdAt: z.string(),
  submitters: AddSubmitterDtoSchema.array().optional(),
});

export const SubmitterTemplateSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  email: z.string().nullable().optional(), // .email(): TSOA won't apply email validation
});

export const SubmissionTemplateSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  status: SubmissionStatusSchema,
  userId: z.string(),
  originalAttachmentOutdated: z.boolean().optional(),
  expirationDate: DateTypeSchema.optional(),
  submissions: SubmissionSchema.array().optional(),
  submitters: SubmitterTemplateSchema.array().optional(),
});

export const DocumentSchema = BaseFirestoreWStatusTagsSchema.extend({
  name: z.string(),
  description: z.string(),
  linkedAttachment: z.string().optional(),
  attachments: AttachmentSchema.array(),
  parentIds: z.string().array().optional(),
  teamId: z.string().optional(),
  confidential: z.boolean().optional(),
  confidentialReason: z.string().or(z.string().array()).optional(),
  visibleOnAgenda: z.boolean().optional(),
  uploadType: UploadTypeIdSchema.optional(),
  ordinanceData: OrdinanceDataSchema.optional(),
  resolutionData: ResolutionDataSchema.optional(),
  minutesData: DocumentMinutesDataSchema.optional(),
  agendaData: DocumentAgendaDataSchema.optional(),
  publishedAt: DateTypeSchema.optional(),
  submissionTemplate: SubmissionTemplateSchema.nullable().optional(),
});

export const PublicDocumentSchema = DocumentSchema.pick({
  _version: true,
  id: true,
  createdAt: true,
  createdBy: true,
  updatedAt: true,
  updatedBy: true,
  name: true,
  description: true,
  attachments: true,
  teamId: true,
  confidential: true,
  confidentialReason: true,
  visibleOnAgenda: true,
  uploadType: true,
  ordinanceData: true,
  resolutionData: true,
  minutesData: true,
  agendaData: true,
  publishedAt: true,
});

// DTOs

export const CreateDocumentDtoSchema = DocumentSchema.partial();

export const PutDocumentDtoSchema = CreateDocumentDtoSchema;

export const PatchDocumentDtoSchema = CreateDocumentDtoSchema;
